import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  MapPin,
  Calendar,
  Package,
  User,
  Truck,
  Building2,
  Edit,
  CheckCircle,
  QrCode,
  FileSignature,
  Lock,
  Eye,
  Loader2,
  Printer,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Image as ImageIcon,
} from 'lucide-react';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { documentsClient, requestsClient, deliveriesClient, unlockWallet, useKeycloak } from '../auth/keycloakProvider';
import { isWalletUnlocked } from '../auth/walletSession';
import { updateDocument } from '../api-clients';
import { getErrorToast } from '../lib/error-utils';
import { ConfirmDeliveryCommand, DocumentDetailDto, SignAndSubmitBlockchainRequestCommand, TransportNotesDto } from '../web-api-client';
import { ECMR, ECMRStatus, TransportNote} from '../types';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { TransportNoteModal } from '../components/ecmr/TransporteNoteModal';

// Labels for string NoteType (used in display)
const noteTypeLabels: Record<'reservation' | 'observation' | 'damage' | 'delay' | 'other', string> = {
    reservation: 'Reserva',
    observation: 'Observação',
    damage: 'Dano',
    delay: 'Atraso',
    other: 'Outro',
};

// Labels for string NoteSeverity (used in display)
const severityLabels: Record<'low' | 'medium' | 'high' | 'critical', string> = {
    low: 'Baixa',
    medium: 'Média',
    high: 'Alta',
    critical: 'Crítica',
};

// Badge variants for string NoteSeverity (used in display)
const severityBadgeVariants: Record<'low' | 'medium' | 'high' | 'critical', 'delivered' | 'pending' | 'transit' | 'cancelled'> = {
    low: 'delivered',
    medium: 'pending',
    high: 'transit',
    critical: 'cancelled',
};

// Helper function to map TransportNotesDto to TransportNote
function mapTransportNotesDtoToTransportNote(dto: TransportNotesDto): TransportNote {
    // Map enum NoteType to string
    const noteTypeMap: Record<number, 'reservation' | 'observation' | 'damage' | 'delay' | 'other'> = {
        0: 'reservation',
        1: 'observation',
        2: 'damage',
        3: 'delay',
        4: 'other',
    };

    // Map enum NoteSeverity to string
    const severityMap: Record<number, 'low' | 'medium' | 'high' | 'critical'> = {
        0: 'low',
        1: 'medium',
        2: 'high',
        3: 'critical',
    };

    // Generate a unique ID using documentId and reportedAt timestamp
    const uniqueId = dto.documentId && dto.reportedAt 
        ? `${dto.documentId}-${new Date(dto.reportedAt).getTime()}`
        : `tn-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return {
        id: uniqueId,
        type: noteTypeMap[dto.noteType ?? 1] || 'observation',
        severity: severityMap[dto.severity ?? 0] || 'low',
        location: dto.location || '',
        description: dto.description,
        imageUrl: dto.imagePath,
        resolved: dto.isResolved ?? false,
        resolution: dto.resolution,
        reportedBy: dto.reportedBy || '',
        reportedAt: dto.reportedAt ? new Date(dto.reportedAt) : new Date(),
    };
}

// Helper function to map DocumentDetailDto to ECMR
function mapDocumentDetailDtoToECMR(document: DocumentDetailDto): ECMR {
  // Map DocumentStatus to ECMRStatus
  const statusMap: Record<string, ECMRStatus> = {
    'Draft': 'draft',
    'Pending': 'pending',
    'InTransit': 'in_transit',
    'Delivered': 'delivered',
    'Signed': 'delivered', // Signed is treated as delivered
    'Cancelled': 'cancelled',
  };

  const status = statusMap[document.status || ''] || 'draft';

  // Map real signatures from the database
  const signatureRoleMap: Record<string, 'sender' | 'carrier' | 'consignee'> = {
    'Sender': 'sender',
    'Carrier': 'carrier',
    'Consignee': 'consignee',
  };

  // Create signature objects based on actual signatures in the database
  const signatures: { role: 'sender' | 'carrier' | 'consignee'; name: string; date: string; signed: boolean }[] = [
    { role: 'sender', name: '', date: '', signed: false },
    { role: 'carrier', name: '', date: '', signed: false },
    { role: 'consignee', name: '', date: '', signed: false },
  ];

  // Fill in signatures from the database
  if (document.signatures && Array.isArray(document.signatures)) {
    document.signatures.forEach((sig: any) => {
      const role = signatureRoleMap[sig.role];
      if (role) {
        const signatureIndex = signatures.findIndex(s => s.role === role);
        if (signatureIndex !== -1) {
          signatures[signatureIndex] = {
            role: role,
            name: sig.signedByName || sig.acceptingParty || '',
            date: sig.signedAt ? new Date(sig.signedAt).toISOString().split('T')[0] : '',
            signed: true,
          };
        }
      }
    });
  }

  return {
    id: document.documentUUID || document.id?.toString() || '',
    numericId: document.id,
    number: document.documentNumber || 'N/A',
    status: status,
    senderName: document.senderName || 'N/A',
    senderAddress: document.originAddress || '',
    senderCountry: '', // Not available in DocumentDetailDto
    consigneeName: document.receiverName || 'N/A',
    consigneeAddress: document.destinationAddress || '',
    consigneeCountry: '', // Not available in DocumentDetailDto
    companyName: document.companyName || 'N/A',
    companyAddress: '', // Not available in DocumentDetailDto
    companyCountry: '', // Not available in DocumentDetailDto
    goodsDescription: document.cargoDescription || '',
    grossWeight: document.cargoWeight,
    packageCount: Number(document.cargoPackaging),
    placeOfTakeover: document.originAddress || '',
    dateOfTakeover: document.created ? new Date(document.created).toISOString().split('T')[0] : '',
    placeOfDelivery: document.destinationAddress || '',
    dateOfDelivery: document.actualDeliveryDate ? new Date(document.actualDeliveryDate).toISOString().split('T')[0] : undefined,
    signatures: signatures,
    deliveryNotes: [], // Not available in DocumentDetailDto - would need separate query
    companyId: document.companyId?.toString() || '',
    createdAt: document.created ? new Date(document.created).toISOString().split('T')[0] : '',
    updatedAt: document.lastModified ? new Date(document.lastModified).toISOString().split('T')[0] : '',
  };
}

export default function ECMRDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useKeycloak();
  const [showQRModal, setShowQRModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Document view / QR Code state
  const [showDocumentViewModal, setShowDocumentViewModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [documentViewRequestUUID, setDocumentViewRequestUUID] = useState<string>('');
  const [signingDocumentView, setSigningDocumentView] = useState(false);
  const [loadingDocumentView, setLoadingDocumentView] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [viewToken, setViewToken] = useState<string | null>(null);
  const [walletPassword, setWalletPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockForAction, setUnlockForAction] = useState<'documentView' | 'update' | null>(null);
  
  // Update document state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateFile, setUpdateFile] = useState<File | null>(null);
  const [updateXmlPreview, setUpdateXmlPreview] = useState<string>('');
  const [updating, setUpdating] = useState(false);
  const [showUpdateSigningModal, setShowUpdateSigningModal] = useState(false);
  const [updateRequestUUID, setUpdateRequestUUID] = useState<string>('');
  const [updateDocumentId, setUpdateDocumentId] = useState<number | null>(null);
  const [signingUpdate, setSigningUpdate] = useState(false);

  const [showTransportNoteModal, setShowTransportNoteModal] = useState(false);
  const [transportNotes, setTransportNotes] = useState<TransportNote[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);

  useEffect(() => {
    const loadDocument = async () => {
      if (!id) {
        setError('ID do documento não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const document = await documentsClient.getDocument(id);
        const mappedEcmr = mapDocumentDetailDtoToECMR(document);
        setEcmr(mappedEcmr);
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Falha ao carregar documento');
        setError(errorToast.description);
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id, toast]);

  useEffect(() => {
    const loadTransportNotes = async () => {
      if (!id) return;

      try {
        const response = await deliveriesClient.getTransportNotes(id);
        if (response.items) {
          const mappedNotes = response.items.map(mapTransportNotesDtoToTransportNote);
          setTransportNotes(mappedNotes);
        }
      } catch (err) {
        console.error('Failed to load transport notes:', err);
        // Don't show error toast for transport notes, just log it
      }
    };

    loadTransportNotes();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner />
        <p className="text-muted-foreground mt-4">A carregar documento...</p>
      </div>
    );
  }

  if (error || !ecmr) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground">{error || 'eCMR não encontrado'}</p>
        <Button variant="link" onClick={() => navigate('/ecmrs')}>
          Voltar aos eCMRs
        </Button>
      </div>
    );
  }

  const handleSign = () => {
    navigate(`/ecmrs/${id}/sign`);
  };

  const handleConfirmDelivery = async () => {
    if (!isWalletUnlocked()) {
      toast({
        title: 'Wallet Bloqueada',
        description: 'Por favor, desbloqueie a wallet para confirmar a entrega.',
        variant: 'destructive',
      });
      return;
    }

    if (!ecmr) return;

    const signedCount = ecmr.signatures.filter(s => s.signed).length;
    if (signedCount < 3) {
      const missing = ecmr.signatures
        .filter(s => !s.signed)
        .map(s => {
          const labels: Record<string, string> = { sender: 'Remetente', carrier: 'Transportador', consignee: 'Destinatário' };
          return labels[s.role] ?? s.role;
        })
        .join(', ');
      toast({
        title: 'Assinaturas em falta',
        description: `O documento ainda não foi assinado por todas as partes. Em falta: ${missing}.`,
        variant: 'destructive',
      });
      return;
    }

    if (!ecmr.numericId) {
      toast({
        title: 'Erro',
        description: 'ID do documento inválido.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setConfirmingDelivery(true);
      await deliveriesClient.confirmDelivery(new ConfirmDeliveryCommand({
        documentId: ecmr.numericId,
        signedByName: user?.name ?? '',
        deliveryDate: new Date(),
        deliveryLocation: ecmr.placeOfDelivery,
      }));
      toast({
        title: 'Entrega Confirmada',
        description: 'A entrega foi confirmada com sucesso.',
      });
      const updated = await documentsClient.getDocument(id!);
      setEcmr(mapDocumentDetailDtoToECMR(updated));
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao confirmar a entrega');
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setConfirmingDelivery(false);
    }
  };

  const handleUpdate = () => {
    setShowUpdateModal(true);
  };

  const handleUpdateFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xml')) {
        toast({
          title: 'Erro',
          description: 'Por favor, selecione um ficheiro XML',
          variant: 'destructive',
        });
        setUpdateFile(null);
        setUpdateXmlPreview('');
        return;
      }

      setUpdateFile(file);
      setError(null);

      // Read and preview the file content
      try {
        const text = await file.text();
        setUpdateXmlPreview(text.substring(0, 500) + (text.length > 500 ? '...' : ''));
        
        // Validate XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, 'text/xml');
        
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          throw new Error('Ficheiro XML inválido');
        }
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Erro ao processar o ficheiro XML');
        setError(errorToast.description);
        setUpdateFile(null);
        setUpdateXmlPreview('');
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      }
    } else {
      setUpdateFile(null);
      setUpdateXmlPreview('');
    }
  };

  const performUpdate = async () => {
    if (!id || !updateFile || !ecmr) return;

    setUpdating(true);
    setError(null);

    try {
      // Read the file content
      const xmlContent = await updateFile.text();
      
      // Update document using the api-clients helper
      const result = await updateDocument({
        documentUUID: id,
        xmlFile: updateFile,
        xmlContent: xmlContent
      });
      
      // Store the request UUID and document ID for blockchain signing
      if (result.requestUUID) {
        setUpdateRequestUUID(result.requestUUID);
        setUpdateDocumentId(result.documentId);
        setShowUpdateSigningModal(true);
        setShowUpdateModal(false);
      } else {
        // If no requestUUID, the document was updated without blockchain signing
        toast({
          title: 'Sucesso',
          description: `eCMR atualizado com sucesso! ID: ${result.documentId}`,
        });
        setShowUpdateModal(false);
        // Reload document
        const updatedDoc = await documentsClient.getDocument(id);
        const mappedEcmr = mapDocumentDetailDtoToECMR(updatedDoc);
        setEcmr(mappedEcmr);
      }
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao atualizar documento');
      setError(err.data);// SMALL FIX. CHECK api-client.ts line 248
      toast({
        title: errorToast.title,
        description: err.data,// SMALL FIX. CHECK api-client.ts line 248
        //description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateSubmit = async () => {
    if (!updateFile) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione um ficheiro XML',
        variant: 'destructive',
      });
      return;
    }

    // Check if wallet is unlocked (backend wallet)
    if (!isWalletUnlocked()) {
      setUnlockForAction('update');
      setShowUnlockModal(true);
      return;
    }

    // Wallet is unlocked, proceed with update
    await performUpdate();
  };

  const handleSignAndSubmitUpdate = async () => {
    if (!updateRequestUUID) return;

    setSigningUpdate(true);
    setError(null);

    try {
      await requestsClient.signAndSubmitBlockchainRequest(new SignAndSubmitBlockchainRequestCommand({
        requestUUID: updateRequestUUID
      }));
      toast({
        title: 'Sucesso',
        description: `eCMR atualizado e registado na blockchain com sucesso! ID: ${updateDocumentId}`,
      });
      setShowUpdateSigningModal(false);
      // Reload document
      const updatedDoc = await documentsClient.getDocument(id!);
      const mappedEcmr = mapDocumentDetailDtoToECMR(updatedDoc);
      setEcmr(mappedEcmr);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao assinar e submeter transação blockchain');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setSigningUpdate(false);
    }
  };

  const handleCancelUpdate = () => {
    setShowUpdateSigningModal(false);
    setUpdateRequestUUID('');
    setUpdateDocumentId(null);
    setError('Atualização cancelada. O documento foi atualizado mas não foi registado na blockchain.');
  };

  const loadPreview = async () => {
    if (!id) return;
    
    setLoadingPreview(true);
    setError(null);
    
    try {
      const response = await documentsClient.showDocument(id);
      setPreviewHtml(response.htmlContent || '');
      setPreviewVersion(response.version || null);
      setShowPreview(true);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao carregar preview do documento');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePrint = () => {
    if (!previewIframeRef.current) {
      toast({
        title: 'Erro',
        description: 'Preview não disponível. Por favor, carregue o preview do documento primeiro.',
        variant: 'destructive',
      });
      return;
    }

    if (!previewHtml) {
      toast({
        title: 'Erro',
        description: 'Nenhum conteúdo de preview disponível para imprimir.',
        variant: 'destructive',
      });
      return;
    }

    try {
      previewIframeRef.current.focus();
      previewIframeRef.current.contentWindow?.print();
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao imprimir documento');
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    }
  };

  const handleUnlockWallet = async () => {
    if (!walletPassword) {
      setError('Por favor, introduza a password da wallet');
      return;
    }

    setUnlocking(true);
    setError(null);

    try {
      await unlockWallet(walletPassword, 60); // 60 minutes session
      setShowUnlockModal(false);
      setWalletPassword('');
      toast({
        title: 'Sucesso',
        description: 'Wallet desbloqueada com sucesso!',
      });
      
      // Small delay to ensure session is fully stored and available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // After unlocking, proceed with the pending action
      if (unlockForAction === 'documentView') {
        await handleGrantDocumentView();
      } else if (unlockForAction === 'update') {
        await performUpdate();
      }
      setUnlockForAction(null);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao desbloquear wallet');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setUnlocking(false);
    }
  };

  const handleGrantDocumentView = async () => {
    if (!id) return;

    // Check if wallet is unlocked
    if (!isWalletUnlocked()) {
      setUnlockForAction('documentView');
      setShowUnlockModal(true);
      return;
    }

    setLoadingDocumentView(true);
    setError(null);
    setQrCodeImage(null);
    setViewToken(null);

    try {
      // Step 1: Call documentView to get requestUUID
      const requestUUID = await documentsClient.viewDocument(id);
      
      if (!requestUUID) {
        throw new Error('Falha ao obter request UUID da visualização do documento');
      }

      setDocumentViewRequestUUID(requestUUID);
      setShowDocumentViewModal(true);
      setShowQRModal(true);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao criar pedido de visualização do documento');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setLoadingDocumentView(false);
    }
  };

  const handleSignAndSubmitDocumentView = async () => {
    if (!documentViewRequestUUID) return;

    setSigningDocumentView(true);
    setError(null);

    try {
      // Sign and submit the blockchain request
      const response = await requestsClient.signAndSubmitBlockchainRequest(
        new SignAndSubmitBlockchainRequestCommand({
          requestUUID: documentViewRequestUUID
        })
      );

      // Extract QR code and token from response (these fields may be in the response even if not in TypeScript types)
      const responseAny = response as any;
      const qrCode = responseAny.tokenQR || responseAny.tokenqr || null;
      const token = responseAny.token || null;

      if (qrCode) {
        setQrCodeImage(qrCode);
        setViewToken(token);
        toast({
          title: 'Sucesso',
          description: 'Acesso à visualização do documento concedido com sucesso! Digitalize o código QR para aceder ao documento.',
        });
      } else {
        toast({
          title: 'Sucesso',
          description: 'Acesso à visualização do documento concedido com sucesso!',
        });
      }
      
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao assinar e submeter pedido de visualização do documento');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setSigningDocumentView(false);
    }
  };

  const handleCancelDocumentView = () => {
    setShowDocumentViewModal(false);
    setShowQRModal(false);
    setDocumentViewRequestUUID('');
    setQrCodeImage(null);
    setViewToken(null);
  };

    const handleAddTransportNote = async (note: Omit<TransportNote, 'id'>) => {
        // Reload transport notes from the backend to get the latest data
        if (!id) return;

        try {
            const response = await deliveriesClient.getTransportNotes(id);
            if (response.items) {
                const mappedNotes = response.items.map(mapTransportNotesDtoToTransportNote);
                setTransportNotes(mappedNotes);
            }
            toast({
                title: 'Nota Adicionada',
                description: 'A nota de transporte foi adicionada com sucesso.',
            });
        } catch (err) {
            console.error('Failed to reload transport notes:', err);
            // Still add to local state as fallback
            const newNote: TransportNote = {
                ...note,
                id: `tn-${Date.now()}`,
            };
            setTransportNotes((prev) => [...prev, newNote]);
            toast({
                title: 'Nota Adicionada',
                description: 'A nota de transporte foi adicionada com sucesso.',
            });
        }
    };

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/ecmrs')}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-semibold tracking-tight">{ecmr.number}</h1>
            <StatusBadge status={ecmr.status} />
          </div>
          <p className="text-muted-foreground">{ecmr.companyName}</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                A carregar...
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Preview
              </>
            )}
          </Button>
          <Button 
            variant="outline" 
            onClick={handleGrantDocumentView}
            disabled={loadingDocumentView}
          >
            {loadingDocumentView ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                A criar...
              </>
            ) : (
              <>
                <QrCode className="h-4 w-4" />
                QR Code
              </>
            )}
            </Button>
            <Button hidden={ecmr.status === 'delivered' || ecmr.status === 'cancelled'} variant="outline" onClick={handleUpdate}>
                {!isWalletUnlocked() && <Lock className="h-4 w-4" />}
                <Edit className="h-4 w-4" />
            Atualizar
            </Button>
          {ecmr.status !== 'delivered' && ecmr.status !== 'cancelled' && (
            <>
              <Button variant="outline" onClick={handleSign}>
                {!isWalletUnlocked() && <Lock className="h-4 w-4" />}
                <FileSignature className="h-4 w-4" />
                Assinar
              </Button>
              <Button
                variant="accent"
                onClick={handleConfirmDelivery}
                disabled={confirmingDelivery || ecmr.signatures.filter(s => s.signed).length < 3}
                title={ecmr.signatures.filter(s => s.signed).length < 3 ? 'O documento precisa de 3 assinaturas (Remetente, Transportador e Destinatário) para confirmar a entrega' : undefined}
              >
                {confirmingDelivery ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {!isWalletUnlocked() && <Lock className="h-4 w-4" />}
                    <CheckCircle className="h-4 w-4" />
                  </>
                )}
                Confirmar Entrega
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Route */}
          <Card className="animate-slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-accent" />
                Rota de Transporte
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <MapPin className="h-4 w-4 text-status-pending" />
                    ORIGEM
                  </div>
                  <p className="font-medium">{ecmr.placeOfTakeover}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {new Date(ecmr.dateOfTakeover).toLocaleDateString('pt-PT')}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <MapPin className="h-4 w-4 text-status-delivered" />
                    DESTINO
                  </div>
                  <p className="font-medium">{ecmr.placeOfDelivery}</p>
                  {ecmr.dateOfDelivery && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {new Date(ecmr.dateOfDelivery).toLocaleDateString('pt-PT')}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Parties */}
          <Card className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-accent" />
                Partes Envolvidas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">EXPEDIDOR</p>
                  <p className="font-medium">{ecmr.senderName}</p>
                  <p className="text-sm text-muted-foreground">{ecmr.senderAddress}</p>
                  <p className="text-sm text-muted-foreground">{ecmr.senderCountry}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">TRANSPORTADOR</p>
                  <p className="font-medium">{ecmr.companyName}</p>
                  <p className="text-sm text-muted-foreground">{ecmr.companyAddress}</p>
                  <p className="text-sm text-muted-foreground">{ecmr.companyCountry}</p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">DESTINATÁRIO</p>
                  <p className="font-medium">{ecmr.consigneeName}</p>
                  <p className="text-sm text-muted-foreground">{ecmr.consigneeAddress}</p>
                  <p className="text-sm text-muted-foreground">{ecmr.consigneeCountry}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Goods */}
          <Card className="animate-slide-up" style={{ animationDelay: '200ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-accent" />
                Mercadoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">{ecmr.goodsDescription}</p>
              <div className="flex flex-wrap gap-4">
                <div className="rounded-lg bg-muted/50 px-4 py-2">
                  <p className="text-sm text-muted-foreground">Volumes</p>
                  <p className="text-lg font-semibold">{ecmr.packageCount}</p>
                </div>
                <div className="rounded-lg bg-muted/50 px-4 py-2">
                  <p className="text-sm text-muted-foreground">Peso Bruto</p>
                  <p className="text-lg font-semibold">{ecmr.grossWeight} kg</p>
                </div>
                {ecmr.volume && (
                  <div className="rounded-lg bg-muted/50 px-4 py-2">
                    <p className="text-sm text-muted-foreground">Volume</p>
                    <p className="text-lg font-semibold">{ecmr.volume} m³</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Signatures */}
          <Card className="animate-slide-up" style={{ animationDelay: '300ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-accent" />
                Assinaturas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ecmr.signatures.map((signature, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                      signature.signed ? 'bg-status-delivered-bg' : 'bg-muted'
                    }`}>
                      <User className={`h-4 w-4 ${
                        signature.signed ? 'text-status-delivered-foreground' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium capitalize">
                        {signature.role === 'sender' && 'Expedidor'}
                        {signature.role === 'carrier' && 'Transportador'}
                        {signature.role === 'consignee' && 'Destinatário'}
                      </p>
                      {signature.signed ? (
                        <p className="text-xs text-muted-foreground">{signature.name}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Pendente</p>
                      )}
                    </div>
                  </div>
                  <Badge variant={signature.signed ? 'delivered' : 'pending'}>
                    {signature.signed ? 'Assinado' : 'Pendente'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
            {/* Transport Notes */}
            <Card className="animate-slide-up" style={{ animationDelay: '400ms' }}>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-accent" />
                        Notas de Transporte
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {transportNotes.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Sem notas de transporte</p>
                    ) : (
                        <div className="space-y-3">
                            {transportNotes.map((note) => (
                                <div key={note.id} className="rounded-lg border p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Badge variant={severityBadgeVariants[note.severity]}>
                                                {severityLabels[note.severity]}
                                            </Badge>
                                            <span className="text-sm font-medium">{noteTypeLabels[note.type]}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {note.resolved ? (
                                                <CheckCircle2 className="h-4 w-4 text-status-delivered-foreground" />
                                            ) : (
                                                <Clock className="h-4 w-4 text-status-pending-foreground" />
                                            )}
                                            {note.imageUrl && (
                                                <button
                                                    onClick={() => setSelectedImage(note.imageUrl!)}
                                                    className="p-1 hover:bg-muted rounded"
                                                >
                                                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <MapPin className="h-3 w-3" />
                                        {note.location}
                                    </div>
                                    {note.description && (
                                        <p className="text-sm text-muted-foreground">{note.description}</p>
                                    )}
                                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t">
                                        <span>Por: {note.reportedBy}</span>
                                        <span>{new Date(note.reportedAt).toLocaleDateString('pt-PT', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}</span>
                                    </div>
                                    {note.resolved && note.resolution && (
                                        <div className="bg-status-delivered-bg rounded p-2 mt-2">
                                            <p className="text-xs font-medium text-status-delivered-foreground">Resolução:</p>
                                            <p className="text-xs text-status-delivered-foreground">{note.resolution}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-4"
                        onClick={() => setShowTransportNoteModal(true)}
                    >
                        Adicionar Nota
                    </Button>
                </CardContent>
            </Card>

            {/* Delivery Notes */}
            <Card className="animate-slide-up" style={{ animationDelay: '500ms' }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-accent" />
                Notas de Entrega
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ecmr.deliveryNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem notas de entrega</p>
              ) : (
                <div className="space-y-3">
                  {ecmr.deliveryNotes.map((note) => (
                    <div key={note.id} className="rounded-lg border p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium">{note.number}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(note.date).toLocaleDateString('pt-PT')}
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground">{note.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Update Document Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Atualizar eCMR</DialogTitle>
            <DialogDescription>
              Carregue um novo ficheiro XML para atualizar este documento. O documento será versionado automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="updateXmlFile">Ficheiro XML *</Label>
              <Input
                id="updateXmlFile"
                type="file"
                accept=".xml"
                onChange={handleUpdateFileChange}
              />
              <p className="text-xs text-muted-foreground">
                Selecione um ficheiro XML contendo os dados atualizados da mensagem eCMR
              </p>
              {updateXmlPreview && (
                <div className="mt-2">
                  <Label className="text-xs text-muted-foreground mb-2 block">Pré-visualização XML (primeiros 500 caracteres):</Label>
                  <pre className="border rounded-lg p-3 bg-muted/50 text-xs overflow-auto max-h-[200px]">
                    {updateXmlPreview}
                  </pre>
                </div>
              )}
            </div>

            {/* Wallet Status */}
            <div className="p-4 rounded-lg bg-muted/50">
              {isWalletUnlocked() ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">🔓 Wallet Desbloqueada</p>
                    <p className="text-xs text-muted-foreground">Pronta para atualizar documentos na blockchain</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">🔒 Wallet Bloqueada</p>
                    <p className="text-xs text-muted-foreground">Será solicitado o desbloqueio ao submeter</p>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUpdateModal(false);
              setUpdateFile(null);
              setUpdateXmlPreview('');
              setError(null);
            }} disabled={updating}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateSubmit} disabled={updating || !updateFile}>
              {updating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A atualizar...
                </>
              ) : (
                'Atualizar eCMR'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wallet Unlock Modal */}
      <Dialog open={showUnlockModal} onOpenChange={setShowUnlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔐 Desbloquear Wallet</DialogTitle>
            <DialogDescription>
              Introduza a password da wallet para desbloquear para operações blockchain.
              A wallet permanecerá desbloqueada durante 60 minutos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="walletPassword">Password da Wallet</Label>
              <Input
                id="walletPassword"
                type="password"
                value={walletPassword}
                onChange={(e) => setWalletPassword(e.target.value)}
                placeholder="Introduza a password"
                onKeyPress={(e) => e.key === 'Enter' && handleUnlockWallet()}
              />
            </div>
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUnlockModal(false);
              setError(null);
              setWalletPassword('');
              setUnlockForAction(null);
            }} disabled={unlocking}>
              Cancelar
            </Button>
            <Button onClick={handleUnlockWallet} disabled={unlocking || !walletPassword}>
              {unlocking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A desbloquear...
                </>
              ) : (
                'Desbloquear Wallet'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document View / QR Code Modal */}
      <Dialog open={showDocumentViewModal && showQRModal} onOpenChange={(open) => {
        if (!open) {
          handleCancelDocumentView();
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Conceder Acesso à Visualização do Documento</DialogTitle>
            <DialogDescription>
              O seu pedido de visualização do documento foi criado e está pronto para ser registado na blockchain.
              Clique no botão abaixo para assinar e submeter a transação blockchain. Após assinar, receberá um código QR que pode ser digitalizado para aceder ao documento.
            </DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="flex flex-col items-center justify-center min-h-[200px] bg-muted/50 rounded-lg p-6">
              {signingDocumentView ? (
                <>
                  <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                  <p className="text-center text-muted-foreground">
                    A assinar e submeter para a blockchain...
                  </p>
                </>
              ) : qrCodeImage ? (
                <>
                  <div className="mb-4">
                    <h5 className="text-center mb-2">Digitalize este código QR para aceder ao documento:</h5>
                  </div>
                  <img 
                    src={qrCodeImage} 
                    alt="Document Access QR Code" 
                    className="max-w-[300px] max-h-[300px] border rounded-lg p-2 bg-white"
                  />
                  {viewToken && (
                    <div className="mt-4 text-center">
                      <p className="text-xs text-muted-foreground">Token: {viewToken.substring(0, 20)}...</p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-5xl mb-4">📄</div>
                  <p className="text-center text-sm text-muted-foreground">
                    Request UUID: {documentViewRequestUUID}
                  </p>
                </>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDocumentView} disabled={signingDocumentView}>
              {qrCodeImage ? 'Fechar' : 'Cancelar'}
            </Button>
            {!qrCodeImage && (
              <Button
                onClick={handleSignAndSubmitDocumentView}
                disabled={signingDocumentView}
              >
                {signingDocumentView ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    A assinar...
                  </>
                ) : (
                  'Assinar e Submeter para Blockchain'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>
                  Preview do Documento eCMR
                  {previewVersion !== null && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      (Versão {previewVersion})
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription>
                  Visualização do documento no formato oficial.
                </DialogDescription>
              </div>
              {previewHtml && !loadingPreview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrint}
                  className="ml-auto"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Imprimir / Exportar PDF
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-hidden py-4">
            {loadingPreview ? (
              <div className="flex flex-col items-center justify-center py-12">
                <LoadingSpinner />
                <p className="text-muted-foreground mt-4">A carregar preview...</p>
              </div>
            ) : previewHtml ? (
              <div className="w-full h-full border rounded-lg overflow-hidden">
                <iframe
                  ref={previewIframeRef}
                  title="Document Preview"
                  srcDoc={previewHtml}
                  className="w-full h-full min-h-[600px] border-0"
                  sandbox="allow-same-origin allow-scripts allow-modals"
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground">Nenhum conteúdo de preview disponível</p>
              </div>
            )}
                </div>
              </DialogContent>
          </Dialog>

          {/* Transport Note Modal */}
          <TransportNoteModal
              open={showTransportNoteModal}
              onOpenChange={setShowTransportNoteModal}
              onSubmit={handleAddTransportNote}
              companyName={ecmr.companyName}
              documentUUID={ecmr.id}
          />

          {/* Image Preview Modal */}
          <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
              <DialogContent className="sm:max-w-2xl">
                  <DialogHeader>
                      <DialogTitle>Imagem da Ocorrência</DialogTitle>
                  </DialogHeader>
                  {selectedImage && (
                      <img
                          src={selectedImage}
                          alt="Imagem da nota"
                          className="w-full rounded-lg"
                      />
                  )}
        </DialogContent>
      </Dialog>

      {/* Update Document Signing Modal */}
      <Dialog open={showUpdateSigningModal} onOpenChange={setShowUpdateSigningModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Completar Registo na Blockchain</DialogTitle>
            <DialogDescription>
              O seu eCMR foi atualizado e está pronto para ser registado na blockchain.
              Clique no botão abaixo para assinar e submeter a transação blockchain.
            </DialogDescription>
          </DialogHeader>
          <div className="py-8">
            {signingUpdate ? (
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-center text-muted-foreground">
                  A assinar e submeter para a blockchain...
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="text-5xl mb-4">📄</div>
                <p className="text-center text-sm text-muted-foreground">
                  ID do Documento: {updateDocumentId}<br />
                  Request UUID: {updateRequestUUID}
                </p>
              </div>
            )}
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelUpdate} disabled={signingUpdate}>
              Cancelar
            </Button>
            <Button onClick={handleSignAndSubmitUpdate} disabled={signingUpdate}>
              {signingUpdate ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A assinar...
                </>
              ) : (
                'Assinar e Submeter para Blockchain'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
